import { useQueryClient } from '@tanstack/react-query'
import { any, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useBeforeGroupChange,
	useCommitOptimizationMutation,
} from '@/entities/optimization'
import {
	useAssignProductsMutation,
	useCreatePricingCampaignMutation,
	useUpdatePricingCampaignMutation,
} from '@/entities/pricing-campaign'

type Props = {
	optimizationGroupId?: string | null
	isScenarioContext: boolean
}

export const usePricingCampaignMutations = ({
	optimizationGroupId,
	isScenarioContext,
}: Props) => {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { onMutate } = useBeforeGroupChange(optimizationGroupId)
	const onSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: ['pricing-campaigns'],
		})
		queryClient.invalidateQueries({
			queryKey: ['pricing-campaigns-overview'],
		})
		queryClient.invalidateQueries({
			queryKey: ['global-pc-sets-list'],
		})
	}
	const assignProducts = useAssignProductsMutation({
		onMutate: !isScenarioContext ? onMutate : () => {},
	})
	const createPricingCampaignMutation = useCreatePricingCampaignMutation({
		onMutate: !isScenarioContext ? onMutate : () => {},
		onSuccess: () => {
			toast.success(
				t('toast.entity.created', { entity: t('entity.pricing_campaign') }),
				{
					duration: 5000,
					id: 'pricing-campaign',
				}
			)
			onSuccess()
		},
	})
	const updatePricingCampaignMutation = useUpdatePricingCampaignMutation({
		onMutate: !isScenarioContext ? onMutate : () => {},
		onSuccess: () => {
			toast.success(
				t('toast.entity.updated', { entity: t('entity.pricing_campaign') }),
				{
					duration: 5000,
					id: 'pricing-campaign',
				}
			)
			onSuccess()
		},
	})
	const commitMutation = useCommitOptimizationMutation()
	const isPending = any(prop('isPending'), [
		createPricingCampaignMutation,
		updatePricingCampaignMutation,
		commitMutation,
	])

	return {
		assignProducts,
		createPricingCampaignMutation,
		updatePricingCampaignMutation,
		commitMutation,
		isPending,
	}
}
